import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, List, Avatar, Spin, Row, Col, Typography, Descriptions, Empty, Select, Button, Space, Tooltip, Collapse, Input, Progress, Statistic } from 'antd';
import {
    BankOutlined,
    EnvironmentOutlined,
    PhoneOutlined,
    MailOutlined,
    GlobalOutlined,
    FilterOutlined,
    ReloadOutlined,
    InfoCircleOutlined,
    DashboardOutlined,
    SearchOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { School } from '../../core/school/types';
import MultiSchoolMap from '../../components/SchoolMap/MultiSchoolMap';
import { schoolTypeOptions, getProgressColor, formatNumber } from '../../core/utils/helpers';
import { loadSingleSchool } from '../../core/school/schoolSlice';
import { AppDispatch, CombinedStates } from '../../core/types';
import { getSchools } from '../../core/school/schoolApi';

const { Title, Text } = Typography;
const { Option } = Select;
const { Panel } = Collapse;
const { Search } = Input;

const AdminDashboard = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const {
        schools,
        isFetching,
    } = useSelector((state:CombinedStates) => state.school);

    const { userData } = useSelector((state: any) => state.user);
    const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
    const [showInfoWindow, setShowInfoWindow] = useState(false);
    const [selectedSchoolType, setSelectedSchoolType] = useState<string | undefined>(undefined);
    const [searchText, setSearchText] = useState<string>('');
    const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');

    useEffect(() => {
        if (userData?.role_id !== 5 && (!schools || schools.length === 0)) {
            dispatch(getSchools({}));
        }
    }, [dispatch, userData?.role_id, schools]);

    // Debounce search text with 0.5 second delay
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchText(searchText);
        }, 500);

        return () => clearTimeout(timer);
    }, [searchText]);

    const handleSchoolClick = (school: School) => {
        setSelectedSchool(school);
        setShowInfoWindow(true);
    };

    const handleInfoWindowClose = () => {
        setShowInfoWindow(false);
    };

    const handleFilterChange = (value: string | undefined) => {
        setSelectedSchoolType(value);
        // Clear selected school if it doesn't match the new filter
        if (selectedSchool && value && selectedSchool.type !== value) {
            setSelectedSchool(null);
            setShowInfoWindow(false);
        }
    };

    const handleResetFilter = () => {
        setSelectedSchoolType(undefined);
    };

    const handleGetAllSchools = () => {
        dispatch(getSchools({}));
        setSelectedSchool(null);
        setShowInfoWindow(false);
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        setSearchText(value);
        // Clear selected school if it doesn't match the new search
        if (selectedSchool && value) {
            const searchLower = value.toLowerCase();
            const schoolMatches = selectedSchool.name.toLowerCase().includes(searchLower)
                || (selectedSchool.email && selectedSchool.email.toLowerCase().includes(searchLower))
                || (selectedSchool.website && selectedSchool.website.toLowerCase().includes(searchLower));

            if (!schoolMatches) {
                setSelectedSchool(null);
                setShowInfoWindow(false);
            }
        }
    };

    const handleClearSearch = () => {
        setSearchText('');
        setDebouncedSearchText('');
    };

    const handleGoToSchoolDashboard = () => {
        if (selectedSchool) {
            dispatch(loadSingleSchool(selectedSchool.id));
            navigate('/mokykla/darbalaukis');
        }
    };

    const getSchoolTypeLabel = (type?: string) => schoolTypeOptions.find((opt) => opt.value === type)?.label || type || 'Nenurodyta';

    // Get appropriate icon and color for school type using schoolTypeOptions
    const getSchoolIcon = (type?: string) => {
        const schoolType = schoolTypeOptions.find(opt => opt.value === type);
        if (schoolType) {
            return {
                icon: <BankOutlined />,
                color: schoolType.color,
            };
        }
        return { icon: <BankOutlined />, color: '#fdab01' }; // default blue
    };

    // Filter schools based on selected type and debounced search text
    const filteredSchools = useMemo(() => {
        let filtered = schools;

        // Filter by school type
        if (selectedSchoolType) {
            filtered = filtered.filter(school => school.type === selectedSchoolType);
        }

        // Filter by debounced search text
        if (debouncedSearchText) {
            const searchLower = debouncedSearchText.toLowerCase();
            filtered = filtered.filter(school => school.name.toLowerCase().includes(searchLower)
                || (school.email && school.email.toLowerCase().includes(searchLower))
                || (school.website && school.website.toLowerCase().includes(searchLower)));
        }

        return filtered;
    }, [schools, selectedSchoolType, debouncedSearchText]);

    const renderColorGuide = () => (
        <Collapse
            ghost
            size="small"
            style={{ marginBottom: '16px' }}
            expandIconPosition="end">
            <Panel
                header={(
                    <Space>
                        <InfoCircleOutlined style={{ color: '#fdab01' }} />
                        <Text strong>Mokyklų tipų spalvų gidas</Text>
                    </Space>
                )}
                key="1">
                <Row gutter={[16, 8]}>
                    {schoolTypeOptions.map((option) => (
                        <Col xs={24} sm={12} md={8} lg={6} key={option.value}>
                            <Space>
                                <Avatar
                                    icon={<BankOutlined />}
                                    style={{ backgroundColor: option.color, width: 20, height: 20 }} />
                                <Text style={{ fontSize: '12px' }}>{option.label}</Text>
                            </Space>
                        </Col>
                    ))}
                </Row>
            </Panel>
        </Collapse>
    );

    const renderSchoolSummary = () => {
        if (!selectedSchool) {
            return (
                <Card>
                    <Empty
                        description="Pasirinkite mokyklą iš sąrašo arba žemėlapio, kad pamatytumėte informaciją"
                        image={Empty.PRESENTED_IMAGE_SIMPLE} />
                </Card>
            );
        }

        return (
            <Card
                title={`${selectedSchool.name} - Informacija`}
                extra={(
                    <Tooltip title="Aplankykite mokyklos darbalaukį">
                        <Button
                            type="primary"
                            icon={<DashboardOutlined />}
                            onClick={handleGoToSchoolDashboard}
                            size="small">
                            Mokyklos darbalaukis
                        </Button>
                    </Tooltip>
                )}>
                <Descriptions column={1} bordered>
                    <Descriptions.Item label="Pavadinimas">
                        {selectedSchool.name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Tipas">
                        {getSchoolTypeLabel(selectedSchool.type)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Adresas">
                        {selectedSchool.address || 'Nenurodyta'}
                    </Descriptions.Item>
                    {selectedSchool.phone && (
                        <Descriptions.Item label="Telefonas">
                            {selectedSchool.phone}
                        </Descriptions.Item>
                    )}
                    {selectedSchool.email && (
                        <Descriptions.Item label="El. paštas">
                            {selectedSchool.email}
                        </Descriptions.Item>
                    )}
                    {selectedSchool.website && (
                        <Descriptions.Item label="Svetainė">
                            <a href={selectedSchool.website} target="_blank" rel="noopener noreferrer">
                                {selectedSchool.website}
                            </a>
                        </Descriptions.Item>
                    )}
                    {selectedSchool.description && (
                        <Descriptions.Item label="Aprašymas">
                            {selectedSchool.description}
                        </Descriptions.Item>
                    )}
                </Descriptions>

                {/* Statistics Section */}
                {selectedSchool.statistics && (
                    <div style={{ marginTop: 24 }}>
                        <Title level={4}>Rodiklių statistika</Title>
                        <Row gutter={16} style={{ marginBottom: 16 }}>
                            <Col span={6}>
                                <Card
                                    style={{ backgroundColor: '#f0f5ff', borderColor: '#2f54eb' }}
                                    bordered={false}>
                                    <Statistic
                                        title="Priskirti rodikliai"
                                        value={formatNumber(selectedSchool.statistics.total_assigned)} />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card
                                    style={{ backgroundColor: '#f6ffed', borderColor: '#52c41a' }}
                                    bordered={false}>
                                    <Statistic
                                        title="Įvesti rodikliai"
                                        value={formatNumber(selectedSchool.statistics.total_filled)} />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card
                                    style={{ backgroundColor: '#fff2e8', borderColor: getProgressColor(selectedSchool.statistics.total_percentage) }}
                                    bordered={false}>
                                    <Statistic
                                        title="Trūkstami rodikliai"
                                        value={formatNumber(selectedSchool.statistics.total_missing)}
                                        valueStyle={{
                                            color: getProgressColor(selectedSchool.statistics.total_percentage),
                                        }} />
                                </Card>
                            </Col>
                            <Col span={6}>
                                <Card
                                    style={{ backgroundColor: '#fffbe6', borderColor: '#faad14' }}
                                    bordered={false}>
                                    <Statistic
                                        title="Kritiniai (įvesta / viso)"
                                        value={`${formatNumber(selectedSchool.statistics.critical_filled)} / ${formatNumber(selectedSchool.statistics.critical_total)}`}
                                        suffix={`(${selectedSchool.statistics.critical_percentage}%)`}
                                        valueStyle={{ color: getProgressColor(selectedSchool.statistics.critical_percentage) }} />
                                </Card>
                            </Col>
                        </Row>

                        {/* Overall progress bar */}
                        <div style={{ marginBottom: 16 }}>
                            <Text strong>Rodiklių užpildymo progresas</Text>
                            <Progress
                                percent={selectedSchool.statistics.total_percentage}
                                strokeColor={getProgressColor(selectedSchool.statistics.total_percentage)}
                                status="active"
                                style={{ marginTop: 8 }} />
                            <Text type="secondary" style={{ fontSize: '12px' }}>
                                Įvesti rodikliai: <strong>{formatNumber(selectedSchool.statistics.total_filled)}</strong> / Iš viso: <strong>{formatNumber(selectedSchool.statistics.total_assigned)}</strong>
                            </Text>
                        </div>

                        {/* Category statistics */}
                        {selectedSchool.statistics.categories && selectedSchool.statistics.categories.length > 0 && (
                            <div>
                                <Text strong>Statistika pagal kategorijas</Text>
                                <div style={{ marginTop: 8 }}>
                                    {selectedSchool.statistics.categories.map((category) => (
                                        <div key={category.category} style={{ marginBottom: 12, padding: '8px 12px', border: '1px solid #f0f0f0', borderRadius: '6px' }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                                <Text strong>{category.category}</Text>
                                                <Text type="secondary">{category.percentage}%</Text>
                                            </div>
                                            <Progress
                                                percent={category.percentage}
                                                strokeColor={getProgressColor(category.percentage)}
                                                size="small"
                                                showInfo={false} />
                                            <Text type="secondary" style={{ fontSize: '11px' }}>
                                                Įvesti: {category.filled} / Iš viso: {category.total} | Trūksta: {category.missing}
                                            </Text>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        );
    };

    const renderMap = () => {
        const schoolsWithCoords = filteredSchools.filter(school => school.latitude && school.longitude);

        if (schoolsWithCoords.length === 0) {
            return (
                <Card title="Mokyklų žemėlapis">
                    <div style={{ height: '400px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Empty description="Nėra mokyklų su koordinatėmis" />
                    </div>
                </Card>
            );
        }

        return (
            <Card title={`Mokyklų žemėlapis (${schoolsWithCoords.length} mokyklos)`}>
                <MultiSchoolMap
                    schools={filteredSchools}
                    selectedSchool={selectedSchool}
                    onSchoolClick={handleSchoolClick}
                    showInfoWindow={showInfoWindow}
                    onInfoWindowClose={handleInfoWindowClose} />
            </Card>
        );
    };

    const renderFilter = () => (
        <Card style={{ marginBottom: '16px' }}>
            <Row gutter={[16, 16]} align="middle">
                <Col flex="auto">
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Space>
                            <FilterOutlined style={{ color: '#fdab01' }} />
                            <Text strong>Filtruoti pagal tipą:</Text>
                            <Select
                                placeholder="Pasirinkite mokyklos tipą"
                                style={{ width: 300 }}
                                value={selectedSchoolType}
                                onChange={handleFilterChange}
                                allowClear>
                                {schoolTypeOptions.map(option => (
                                    <Option key={option.value} value={option.value}>
                                        <Space>
                                            <Avatar
                                                icon={<BankOutlined />}
                                                style={{ backgroundColor: option.color, width: 16, height: 16 }} />
                                            {option.label}
                                        </Space>
                                    </Option>
                                ))}
                            </Select>
                            <Button
                                type="primary"
                                icon={<ReloadOutlined />}
                                onClick={handleResetFilter}
                                disabled={!selectedSchoolType}>
                                Rodyti visas
                            </Button>
                            <Button
                                type="default"
                                icon={<ReloadOutlined />}
                                onClick={handleGetAllSchools}>
                                Atnaujinti sąrašą
                            </Button>
                        </Space>
                        <Space>
                            <SearchOutlined style={{ color: '#fdab01' }} />
                            <Text strong>Ieškoti:</Text>
                            <Search
                                placeholder="Ieškoti pagal pavadinimą, adresą, el. paštą ar svetainę"
                                value={searchText}
                                onChange={handleSearchChange}
                                onClear={handleClearSearch}
                                allowClear
                                style={{ width: 300 }} />
                        </Space>
                    </Space>
                </Col>
                <Col flex="none">
                    {selectedSchool && (
                        <Tooltip title="Aplankykite mokyklos darbalaukį">
                            <Button
                                type="primary"
                                icon={<DashboardOutlined />}
                                onClick={handleGoToSchoolDashboard}
                                size="middle">
                                {selectedSchool.name}
                            </Button>
                        </Tooltip>
                    )}
                </Col>
            </Row>
            {(selectedSchoolType || debouncedSearchText) && (
                <div style={{ marginTop: '8px' }}>
                    <Text type="secondary">
                        Rodoma: {filteredSchools.length} iš {schools.length} mokyklų
                        {selectedSchoolType && ` (${getSchoolTypeLabel(selectedSchoolType)})`}
                        {debouncedSearchText && ` (paieška: "${debouncedSearchText}")`}
                    </Text>
                </div>
            )}
        </Card>
    );

    return (
        <div style={{ marginTop: '20px' }}>
            {renderColorGuide()}
            {renderFilter()}
            <Row gutter={[24, 24]}>
                {/* Left side - Schools list */}
                <Col xs={24} lg={12}>
                    <Card loading={isFetching} title={`Mokyklos (${filteredSchools.length}${selectedSchoolType ? ` - ${getSchoolTypeLabel(selectedSchoolType)}` : ''}${debouncedSearchText ? ` - paieška: "${debouncedSearchText}"` : ''})`}>
                        <List
                            dataSource={filteredSchools}
                            renderItem={(school) => {
                                const schoolIcon = getSchoolIcon(school.type);
                                return (
                                    <List.Item
                                        onClick={() => handleSchoolClick(school)}
                                        style={{
                                            cursor: 'pointer',
                                            backgroundColor: selectedSchool?.id === school.id ? '#f0f0f0' : 'transparent',
                                            padding: '12px',
                                            borderRadius: '6px',
                                            transition: 'background-color 0.2s',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = selectedSchool?.id === school.id ? '#f0f0f0' : '#fafafa';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = selectedSchool?.id === school.id ? '#f0f0f0' : 'transparent';
                                        }}>
                                        <List.Item.Meta
                                            avatar={(
                                                <Avatar
                                                    icon={schoolIcon.icon}
                                                    style={{ backgroundColor: schoolIcon.color }} />
                                            )}
                                            title={(
                                                <Text strong>
                                                    {school.name}
                                                </Text>
                                            )}
                                            description={(
                                                <div>
                                                    <div>
                                                        <EnvironmentOutlined style={{ marginRight: '4px' }} />
                                                        {school.address || 'Adresas nenurodytas'}
                                                    </div>
                                                    <div>
                                                        <Text type="secondary">
                                                            {getSchoolTypeLabel(school.type)}
                                                        </Text>
                                                    </div>
                                                </div>
                                            )} />
                                    </List.Item>
                                );
                            }} />
                    </Card>
                </Col>

                {/* Right side - Map and School Summary */}
                <Col xs={24} lg={12}>
                    {renderMap()}

                    <div style={{ marginTop: '24px' }}>
                        {renderSchoolSummary()}
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default AdminDashboard;
